import React, { useState, useCallback, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import Resizer from 'react-image-file-resizer';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/Card';
import { X, Crop, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-image-crop/dist/ReactCrop.css';

// Proporção ideal: 3508 x 1665 (aproximadamente 2.1069:1)
const TARGET_ASPECT_RATIO = 3508 / 1665; 

// Função auxiliar para centralizar o corte
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 90,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    );
}

// Função para obter o Blob da imagem cortada
const getCroppedImageBlob = (image, crop) => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        
        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height,
        );

        // Converte o canvas para Blob (formato JPEG para melhor compressão)
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Falha ao criar blob da imagem.'));
                return;
            }
            
            // Calcula a altura proporcional para a largura máxima de 1920px
            const MAX_WIDTH = 1920;
            const MAX_HEIGHT = Math.round(MAX_WIDTH / TARGET_ASPECT_RATIO); // 1920 / 2.1069 ≈ 911px

            // Redimensiona o blob para a largura máxima de 1920px, mantendo a proporção
            Resizer.imageFileResizer(
                blob,
                MAX_WIDTH, // Largura máxima
                MAX_HEIGHT, // Altura máxima (garantida pela proporção)
                'JPEG', // Formato de saída
                90, // Qualidade (0-100)
                0, // Rotação
                (uri) => {
                    // uri é o Blob redimensionado
                    resolve(uri);
                },
                'blob' // Tipo de saída
            );
        }, 'image/jpeg', 0.9);
    });
};


function ImageCropperModal({ isOpen, onClose, file, onCropComplete }) {
    const [src, setSrc] = useState(null);
    const [imageRef, setImageRef] = useState(null);
    const [crop, setCrop] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (isOpen && file) {
            const reader = new FileReader();
            reader.addEventListener('load', () => setSrc(reader.result));
            reader.readAsDataURL(file);
        } else {
            setSrc(null);
            setImageRef(null);
            setCrop(null);
        }
    }, [isOpen, file]);

    if (!isOpen || !file) {
        return null;
    }

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        setImageRef(e.currentTarget);
        setCrop(centerAspectCrop(width, height, TARGET_ASPECT_RATIO));
    };

    const handleCrop = async () => {
        if (!imageRef || !crop || isProcessing) {
            toast.error("Selecione uma área de corte válida.");
            return;
        }

        setIsProcessing(true);
        try {
            const croppedBlob = await getCroppedImageBlob(imageRef, crop);
            onCropComplete(croppedBlob);
            onClose();
        } catch (error) {
            console.error("Erro ao cortar imagem:", error);
            toast.error(error.message || "Erro ao processar a imagem.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-[60] flex justify-center items-center p-4">
            <Card className="w-full max-w-4xl relative max-h-[95vh] flex flex-col">
                <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors z-10">
                    <X className="h-5 w-5" />
                </button>
                <CardHeader>
                    <CardTitle className="mb-2 flex items-center"><Crop className="mr-2 h-5 w-5" /> Cortar Imagem (Proporção 3508:1665)</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Ajuste a área de corte para garantir que a imagem do evento tenha a proporção ideal (3508x1665).
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto p-4">
                    {src && (
                        <div className="flex justify-center items-center bg-muted p-2 rounded-md">
                            <ReactCrop
                                crop={crop}
                                onChange={c => setCrop(c)}
                                aspect={TARGET_ASPECT_RATIO}
                                minWidth={100}
                                minHeight={100 / TARGET_ASPECT_RATIO}
                                ruleOfThirds
                            >
                                <img 
                                    src={src} 
                                    onLoad={onImageLoad} 
                                    alt="Imagem para corte" 
                                    className="max-w-full max-h-[60vh] object-contain"
                                />
                            </ReactCrop>
                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-end gap-2 border-t pt-4">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isProcessing}>
                        Cancelar
                    </Button>
                    <Button type="button" onClick={handleCrop} disabled={isProcessing || !crop}>
                        {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crop className="mr-2 h-4 w-4" />}
                        {isProcessing ? 'A processar...' : 'Cortar e Carregar'}
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}

export default ImageCropperModal;